import { PressEvent } from '@react-types/shared'
import { Button } from 'blaze-ui/Button'
import { useDeleteFont } from 'hooks/queries/workspaces/useDeleteFont'
import { Font, FontRole, TextStyle } from 'types'
import { Modal, StackModalProps, useModals } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import { useCallback } from 'react'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { IconButtonProps } from 'blaze-ui/IconButton/Types'
import { Trash2 } from 'blaze-ui/icons'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { FontInUseModal } from './FontInUseModal'

export interface ConfirmModalProps extends StackModalProps {
  onCancel: () => void
  onConfirm: () => void
  onPressOutside: () => void
  font: Font
}

const ConfirmModal = ({ onCancel, onConfirm, onPressOutside, font }: ConfirmModalProps) => (
  <Modal.Root onPressOutside={onPressOutside}>
    <Modal.Header title="Confirmation" />
    <Modal.Content>
      <Text>
        Are you sure you want to delete the font <strong>{font.name}</strong>? Designs that use this font will render
        incorrectly.
      </Text>
    </Modal.Content>
    <Modal.Footer>
      <Modal.FooterContent slot="left">
        <Button onPress={onCancel} size="lg" variant="tertiary">
          Cancel
        </Button>
      </Modal.FooterContent>
      <Modal.FooterContent slot="right">
        <Button onPress={onConfirm} size="lg" variant="primary">
          Confirm
        </Button>
      </Modal.FooterContent>
    </Modal.Footer>
  </Modal.Root>
)

interface Props extends IconButtonProps {
  font: Font
  textStyles?: TextStyle[]
}

export const DeleteFontButton = ({ font, textStyles, onPress, ...buttonProps }: Props) => {
  const deleteFontMutation = useDeleteFont(
    { id: font.id },
    {
      onError: () => {
        showToastMessage(
          'Unable to delete font. Make sure it is not currently in use as a title or body font.',
          'error',
        )
      },
    },
  )
  const { openModal, closeModal } = useModals()
  const confirmDeletion = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        const usedBy = textStyles ? getTextStylesUsingFont(textStyles, font.name) : []

        if (usedBy.length > 0) {
          openModal(
            FontInUseModal,
            {
              font,
              usedByTextStyles: usedBy,
              onClose: () => {
                resolve(false)
                closeModal()
              },
              onPressOutside: () => {
                resolve(false)
                closeModal()
              },
            },
            { newStack: true },
          )
          return
        }

        const props = {
          font,
          onCancel: () => {
            resolve(false)
            closeModal()
          },
          onConfirm: () => {
            resolve(true)
            closeModal()
          },
          onPressOutside: () => {
            resolve(false)
            closeModal()
          },
        }
        openModal(ConfirmModal, props, { newStack: true })
      }),
    [textStyles, font, openModal, closeModal],
  )

  const handlePress = async (e: PressEvent) => {
    onPress?.(e)
    const confirmed = await confirmDeletion()
    if (!confirmed) {
      return
    }
    try {
      await deleteFontMutation.mutateAsync()
    } catch {
      return
    }
  }

  return <IconButton aria-label={`Delete font ${font.name}`} {...buttonProps} onPress={handlePress} icon={Trash2} />
}

const getTextStylesUsingFont = (textStyles: TextStyle[], fontName: string): TextStyle[] => textStyles.filter(
  (ts) => ts.style.font_family === fontName && (ts.font_role === FontRole.Title || ts.font_role === FontRole.Body),
)